import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import {
  CounterFormSchema,
  CounterSchemaT,
} from "../../types/schema/counterSchema";
import { Button } from "../../utils/components/ui/button";
import { Input } from "../../utils/components/ui/input";
import { Label } from "../../utils/components/ui/label";
import {
  Select,
  SelectContent,
  SelectTrigger,
  SelectItem,
  SelectValue,
} from "../../utils/components/ui/select";
import { useGetAllBranches } from "../../service/branchService";
import { useAddCounter, useEditCounter } from "../../service/counterService";
import { Counter } from "../../types/counterTypes";

const buildInitialFormData = (counter?: Counter): CounterSchemaT => ({
  counterName: counter?.counterName ?? "",
  branchId: counter?.branchId ?? counter?.branchResponse?.branchId ?? 0,
  isActive: counter?.isActive ?? true,
});

interface AddEditCounterFormProps {
  onClose: () => void;
  editCounter?: Counter;
}

const AddEditCounterForm = ({
  onClose,
  editCounter,
}: AddEditCounterFormProps) => {
  const isEditMode = !!editCounter;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<CounterSchemaT>({
    resolver: zodResolver(CounterFormSchema),
    defaultValues: buildInitialFormData(editCounter),
  });

  const { data } = useGetAllBranches({
    params: {
      search: "",
      isActive: true,
      page: 0,
      size: 100,
      sortBy: "branchName",
      sortDir: "asc",
    },
  });

  const queryClient = useQueryClient();
  const addCounterMutation = useAddCounter();
  const editCounterMutation = useEditCounter();

  const isPending =
    addCounterMutation.isPending || editCounterMutation.isPending;

  useEffect(() => {
    reset(buildInitialFormData(editCounter));
  }, [editCounter, reset]);

  const onFormSubmit = (formValues: CounterSchemaT) => {
    const payload: CounterSchemaT = {
      ...formValues,
      counterName: formValues.counterName.trim(),
    };

    const handleSuccess = () => {
      toast.success(
        isEditMode
          ? "Counter updated successfully!"
          : "Counter saved successfully!",
      );
      queryClient.invalidateQueries({ queryKey: ["useGetAllCounters"] });
      onClose();
      reset(buildInitialFormData());
    };

    const handleError = (error: Error) => {
      toast.error(
        isEditMode
          ? `Failed to update counter: ${error.message}`
          : `Failed to save counter: ${error.message}`,
      );
    };

    if (isEditMode && editCounter) {
      editCounterMutation.mutate(
        { counterId: editCounter.id, postData: payload },
        { onSuccess: handleSuccess, onError: handleError },
      );
    } else {
      addCounterMutation.mutate(payload, {
        onSuccess: handleSuccess,
        onError: handleError,
      });
    }
  };

  return (
    <div className="w-[min(100vw,450px)]">
      <h2 className="mb-4 text-xl font-semibold text-gray-900">
        {isEditMode ? "Edit Counter" : "Add Counter"}
      </h2>

      <form className="space-y-4" onSubmit={handleSubmit(onFormSubmit)}>
        <div className="space-y-1">
          <Label htmlFor="counterName">Counter Name</Label>
          <Input
            id="counterName"
            placeholder="Enter counter name"
            {...register("counterName")}
            required
          />
          {errors.counterName && (
            <p className="text-xs text-red-600">{errors.counterName.message}</p>
          )}
        </div>

        <div className="space-y-1">
          <Label htmlFor="branchId">Branch</Label>
          <Controller
            control={control}
            name="branchId"
            render={({ field }) => (
              <Select
                value={field.value ? String(field.value) : undefined}
                onValueChange={(value) => field.onChange(Number(value))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select branch" />
                </SelectTrigger>
                <SelectContent>
                  {data?.content?.map((branch) => (
                    <SelectItem
                      key={branch.branchId}
                      value={String(branch.branchId)}
                    >
                      {branch.branchName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {errors.branchId && (
            <p className="text-xs text-red-600">{errors.branchId.message}</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Input
            id="isActive"
            type="checkbox"
            {...register("isActive")}
            className="h-4 w-4"
          />
          <Label htmlFor="isActive">Is Active</Label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onClose();
              reset(buildInitialFormData());
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isPending}
            className="bg-[#1A5EF9] text-white hover:bg-[#0E4FDE]"
          >
            {isPending
              ? isEditMode
                ? "Saving..."
                : "Adding..."
              : isEditMode
                ? "Save Changes"
                : "Save Counter"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddEditCounterForm;
